// Session access for the play screens. Inside the shell's SessionProvider this reads its context; outside it (tests,
// or a screen mounted on its own) it falls back to a one-shot fetch of /session cached per page load.
import { useEffect, useState } from "react";
import { DEFAULT_PREFS, type Prefs, type Session } from "@digu/shared";
import { useSession as useShellSession, type SessionState } from "../../lib/session";
import { api } from "./net.js";

let cached: Session | null = null;
let inflight: Promise<Session | null> | null = null;
const listeners = new Set<(s: Session | null) => void>();

function loadSession(): Promise<Session | null> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = api<Session>("GET", "/session")
      .then((s) => { if (!s || typeof s !== "object" || !("account" in s)) return null; cached = s; listeners.forEach((l) => l(s)); return s; })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Lets tests seed the fallback session without a network call. */
export function seedSession(s: Session | null): void { cached = s; listeners.forEach((l) => l(s)); }

function useShellSessionIfAny(): SessionState | null {
  // useShellSession calls useContext unconditionally and throws outside the provider; the hook order is stable either way.
  try { return useShellSession(); } catch { return null; }
}

function useFallbackSession(active: boolean): Session | null {
  const [s, setS] = useState<Session | null>(cached);
  useEffect(() => {
    if (!active) return;
    listeners.add(setS);
    if (!cached) void loadSession();
    return () => { listeners.delete(setS); };
  }, [active]);
  return s;
}

export interface PlaySession { account: Session["account"] | null; prefs: Prefs; membership: boolean; /** Present inside the shell; the fallback session is read-only. */ setPrefs?: (patch: Partial<Prefs>) => void }

export function useSession(): PlaySession {
  const shell = useShellSessionIfAny();
  const fallback = useFallbackSession(shell === null);
  if (shell) return { account: shell.account, prefs: shell.prefs, membership: shell.account?.membership.active ?? false, setPrefs: shell.setPrefs };
  return { account: fallback?.account ?? null, prefs: fallback?.prefs ?? DEFAULT_PREFS, membership: fallback?.account?.membership?.active ?? false };
}

export function usePrefs(): Prefs { return useSession().prefs; }
